// Joint Constraints (in degrees)
// [xMin, xMax], [yMin, yMax], [zMin, zMax]
// Default rotation order is XYZ:
// X = Pitch 
//   - For Upwards joints (Torso, Stomach, Neck): +X = Forward, -X = Backward
//   - For Downwards joints (Arms, Legs): +X = Backward, -X = Forward
// Y = Yaw (+Y = Leftward twist / Internal rotation)
// Z = Roll (+Z = Leftward tilt / Abduction)
//
// NOTE: XYZ is used globally as it provides the most intuitive control 
// and avoids Gimbal Lock in the most common "Flexion" poses (X=90).

export interface Range {
  x: [number, number];
  y: [number, number];
  z: [number, number];
}

export const JOINT_ROTATION_ORDERS: Record<string, string> = {
  neck: 'XZY',          // Make Y (Twist) the innermost axis for independent looking
  leftShoulder: 'XYZ',  // Explicitly keep Z (Roll/Abduction) as innermost child
  rightShoulder: 'XYZ'
};

export const getJointRotationOrder = (jointName: string): string => {
  return JOINT_ROTATION_ORDERS[jointName] || 'XYZ';
};

export const JOINT_CONSTRAINTS: Record<string, Range> = {
  neck: {
    x: [-45, 90],  // Backward (Extension) to Forward (Flexion)
    y: [-90, 90],  // Symmetric Rotation (No motion)
    z: [-60, 60]   // Side Tilt (Ear to shoulder)
  },
  head: {
    x: [-30, 30],  // Fine-tuned Nodding (C0-C1)
    y: [-15, 15],  // Small upper rotation
    z: [-15, 15]     // Tiny skull tilt
  },
  stomach: {
    x: [-30, 60],  // Backward extension to Forward flexion (+X is forward)
    y: [-45, 45],
    z: [-25, 25]
  },
  chest: {
    x: [-30, 60],  // Backward extension to Forward flexion (+X is forward)
    y: [-45, 45],  // Axial twist
    z: [-25, 25]   // Lateral bend
  },
  torso: {
    x: [-25, 180], // Backward extension to Full forward bend (+X is forward)
    y: [-90, 90],  // Symmetric Twist
    z: [-90, 90]   // Symmetric Pelvic Tilt
  },
  leftClavicle: {
    x: [-15, 30],  // Forward to Backward (+X is backward for side joints)
    y: [-20, 20],
    z: [-30, 40]   // Down to Up
  },
  rightClavicle: {
    x: [-15, 30],
    y: [-20, 20],
    z: [-40, 30]   // Mirrored
  },
  leftShoulder: {
    x: [-180, 60], // Forward (Flexion) to Backward (Extension)
    y: [-90, 90],  // Internal to External rotation
    z: [-50, 180]  // Adduction (Across) to Abduction (Side Up)
  },
  rightShoulder: {
    x: [-180, 60],
    y: [-90, 90],
    z: [-180, 50]  // Mirrored
  },
  leftElbow: {
    x: [-150, 0],  // Flexion (Forward) to Straight
    y: [0, 0],
    z: [0, 0]
  },
  rightElbow: {
    x: [-150, 0],
    y: [0, 0],
    z: [0, 0]
  },
  leftHip: {
    x: [-180, 20], // Flexion (Forward) to Extension (Backward)
    y: [-45, 45],  // Rotation
    z: [-30, 90]   // Adduction to Abduction
  },
  rightHip: {
    x: [-180, 20],
    y: [-45, 45],
    z: [-90, 30]   // Mirrored
  },
  leftKnee: {
    x: [0, 180],   // Straight to Flexion (Backward)
    y: [0, 0],
    z: [0, 0]
  },
  rightKnee: {
    x: [0, 180],
    y: [0, 0],
    z: [0, 0]
  },
  leftPalm: {
    x: [-80, 70],  // Flexion (Forward) to Extension (Backward)
    y: [-20, 20],
    z: [-30, 30]
  },
  rightPalm: {
    x: [-80, 70],
    y: [-20, 20],
    z: [-30, 30]
  },
  leftFoot: {
    x: [-20, 50],  // Dorsi to Plantar flexion
    y: [-10, 10],
    z: [-30, 20]
  },
  rightFoot: {
    x: [-20, 50],
    y: [-10, 10],
    z: [-20, 30]
  },
  elevation: {
    x: [-40, 40],
    y: [-2, 3], // Min will be dynamic, -2 is a fallback
    z: [-40, 40]
  },
  orientation: {
    x: [-180, 180],
    y: [-180, 180],
    z: [-180, 180]
  }
};

export const degToRad = (degrees: number) => degrees * (Math.PI / 180);
export const radToDeg = (radians: number) => radians * (180 / Math.PI);

export const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
